import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { useTranslation } from "locales";
import { StyleFunction, useThemedStyle, StyleData } from "../../theme";
import { Text } from "../text";
import { Button } from "../button";
import { ModalWrapper, useModal } from "../modal";

type ConfirmModalProps = {
    headerText: string;
    bodyText: string | ReactElement;
    onClose: (val: boolean) => void;
    timeout?: number;

    /**
     * This arguments determines if the modal should "cancel" or "confirm" at the end of the timeout.
     *
     * Options: true = confirm, false = cancel
     * Default: true = confirm
     */
    confirmOnTimeout?: boolean;
    testID?: string;
};

export function ConfirmModal({
    headerText,
    bodyText,
    onClose,
    timeout = 0,
    confirmOnTimeout = true,
    testID,
}: ConfirmModalProps) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [timeRemaining, setTimeRemaining] = useState<number | undefined>(
        timeout
    );

    const handleCloseModal = useCallback(() => {
        return onClose(confirmOnTimeout);
    }, [confirmOnTimeout, onClose]);

    useEffect(() => {
        if (!timeout || timeRemaining === undefined) {
            return;
        }

        let timer = setInterval(() => {
            if (timeRemaining === 0) {
                clearInterval(timer);
                handleCloseModal();
            } else {
                setTimeRemaining(timeRemaining - 1);
            }
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [handleCloseModal, timeRemaining, timeout]);
    return (
        <ModalWrapper
            onClose={() => onClose(false)}
            showCloseIcon={false}
            title={headerText}
            containerStyle={styles.modalContainer}
            testID={testID}
        >
            <View
                style={[
                    styles.container,
                    headerText ? styles.resetTopPadding : null,
                ]}
            >
                {typeof bodyText === "string" ? (
                    <Text>{bodyText}</Text>
                ) : (
                    bodyText
                )}
            </View>

            <View style={styles.buttonContainer}>
                <Button
                    onPress={() => {
                        onClose(false);
                    }}
                    style={styles.buttonCancel}
                    testID="modal:cancel"
                >
                    {t("common.cancel", "Cancel")}
                    {!!timeout &&
                        !confirmOnTimeout &&
                        confirmOnTimeout !== undefined &&
                        ` (${timeRemaining})`}
                </Button>
                <Button
                    type="primary"
                    onPress={() => {
                        onClose(true);
                    }}
                    style={styles.buttonConfirm}
                    testID="modal:confirm"
                >
                    {t("backoffice.confirm", "Accept")}
                    {!!timeout &&
                        (confirmOnTimeout === undefined || confirmOnTimeout) &&
                        ` (${timeRemaining})`}
                </Button>
            </View>
        </ModalWrapper>
    );
}

const styleFunc: StyleFunction = (theme, dimensions) => {
    let styling: StyleData = {
        // Cap the whole modal (not just the body) so that a long header
        // cannot make the modal wider than the body's design width.
        modalContainer: {
            maxWidth:
                dimensions.width <= theme.breakpoint.portraitWidth
                    ? dimensions.width - theme.spacingScale * 2
                    : 450,
        },
        container: {
            padding: theme.spacingScale * 2,
            // Only effective in landscape; in portrait the block below
            // replaces this with a fixed width and maxWidth: "auto".
            maxWidth: 450,
        },
        buttonContainer: {
            flexDirection: "row",
            gap: 1,
        },
        resetTopPadding: {
            paddingTop: 0,
        },
        buttonCancel: {
            flex: 1,
            borderRadius: 0,
            borderBottomLeftRadius: theme.borderRadius,
        },
        buttonConfirm: {
            flex: 1,
            borderRadius: 0,
            borderBottomRightRadius: theme.borderRadius,
        },
    };

    if (dimensions.width <= theme.breakpoint.portraitWidth) {
        styling = {
            ...styling,

            container: {
                ...styling.container,
                width: dimensions.width - theme.spacingScale * 2,
                maxWidth: "auto",
            },
        };
    }

    return styling;
};

export function useConfirm() {
    const { render } = useModal<boolean>();

    return useCallback(
        (
            headerText: ConfirmModalProps["headerText"],
            bodyText: ConfirmModalProps["bodyText"],
            timeout?: ConfirmModalProps["timeout"],
            confirmOnTimeout?: ConfirmModalProps["confirmOnTimeout"],
            testID?: string
        ) =>
            render(onClose => (
                <ConfirmModal
                    headerText={headerText}
                    bodyText={bodyText}
                    onClose={onClose}
                    timeout={timeout}
                    confirmOnTimeout={confirmOnTimeout}
                    testID={testID}
                />
            )),
        [render]
    );
}
